import { useCallback, useEffect, useRef, useMemo } from 'react';
import { useRouter } from 'next/router';

import { useURLState } from 'contexts/URLStateContext';

type BwhAlgorithm = 'RAYTRAC' | 'SPECTRAL_REFRACTION' | 'POLYNOMIAL';
type KbygQueryParamsOut = Record<string, string | boolean>;
type KbygQueryIn = Record<string, string | boolean | string[] | undefined> & {
  bwhAlgorithm?: BwhAlgorithm;
};

export const getKbygQueryParams = (queryObject: KbygQueryIn = {}): KbygQueryParamsOut => {
  const rawEntries = Object.entries(queryObject).filter(([key]) => key.includes('kbyg_'));
  const normalizedEntries: Array<[string, string | boolean]> = rawEntries
    .map(([key, value]) => {
      if (Array.isArray(value)) return [key, value[0] ?? ''];
      if (value === undefined) return undefined as unknown as [string, string | boolean];
      return [key, value];
    })
    .filter(Boolean) as Array<[string, string | boolean]>;

  const algo = (queryObject as { bwhAlgorithm?: BwhAlgorithm }).bwhAlgorithm;
  if (algo) {
    normalizedEntries.push(['bwhAlgorithm', algo]);
  }
  return Object.fromEntries(normalizedEntries);
};

export interface Props {
  cameras: Array<{ _id: string }>;
  isMultiCam: boolean | undefined;
  isWavePool?: boolean;
  multiCamLoaded?: boolean;
  primaryCamId: string | undefined;
  shouldUpdateUrlCamId?: boolean;
  spotId: string;
  spotName: string;
}

const useCameraURLManagement = ({
  cameras,
  isMultiCam,
  isWavePool,
  multiCamLoaded = true,
  primaryCamId,
  shouldUpdateUrlCamId = true,
  spotId,
  spotName,
}: Props) => {
  const router = useRouter();
  const { updateCameraId } = useURLState();

  const isNavigatingRef = useRef(false);
  const cameraData = useMemo(
    () => ({
      camerasLength: cameras.length,
      firstCameraId: cameras?.[0]?._id,
      hasCameras: cameras.length > 0,
    }),
    [cameras],
  );

  const updateCamId = useCallback(
    (camIdValue: string | undefined) => {
      updateCameraId({
        camId: camIdValue,
        spotId,
        spotName,
        isWavePool,
      });
    },
    [updateCameraId, spotId, spotName, isWavePool],
  );

  useEffect(() => {
    const handleRouteChangeStart = () => {
      isNavigatingRef.current = true;
    };

    const handleRouteChangeComplete = () => {
      isNavigatingRef.current = false;
    };

    const handleRouteChangeError = () => {
      isNavigatingRef.current = false;
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    router.events.on('routeChangeError', handleRouteChangeError);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
      router.events.off('routeChangeError', handleRouteChangeError);
    };
  }, [router.events]);

  useEffect(() => {
    if (!shouldUpdateUrlCamId || !router.isReady || router.isFallback || !multiCamLoaded) {
      return;
    }

    if (!spotId || !spotName) {
      return;
    }

    if (router.query.subregionForecast) {
      return;
    }

    if (!isMultiCam && cameraData.camerasLength > 1) {
      const camIdToUse = primaryCamId || cameraData.firstCameraId;
      if (camIdToUse) {
        updateCamId(camIdToUse);
      }
      return;
    }

    if (isMultiCam) {
      const currentUrlCamId = router.query.camId as string;
      if (!currentUrlCamId || currentUrlCamId === primaryCamId) {
        updateCamId(undefined);
      }
    }
  }, [
    shouldUpdateUrlCamId,
    router.isReady,
    router.isFallback,
    router.query.subregionForecast,
    router.query.camId,
    multiCamLoaded,
    spotId,
    spotName,
    isMultiCam,
    primaryCamId,
    cameraData.camerasLength,
    cameraData.firstCameraId,
    updateCamId,
  ]);

  return null;
};

export default useCameraURLManagement;
